import { type Observable } from "core/Misc/observable";
import { Tools } from "../Misc/tools.pure";
import { type IDisposable } from "../scene";
import { type IWebXRAnchorSystemOptions, type WebXRAnchorSystem } from "./features/WebXRAnchorSystem";
import { type IWebXRBackgroundRemoverOptions, type WebXRBackgroundRemover } from "./features/WebXRBackgroundRemover";
import { type IWebXRBodyTrackingOptions, type WebXRBodyTracking } from "./features/WebXRBodyTracking";
import { type IWebXRControllerMovementOptions, type WebXRControllerMovement } from "./features/WebXRControllerMovement";
import { type IWebXRControllerPhysicsOptions, type WebXRControllerPhysics } from "./features/WebXRControllerPhysics";
import { type IWebXRControllerPointerSelectionOptions, type WebXRControllerPointerSelection } from "./features/WebXRControllerPointerSelection";
import { type IWebXRTeleportationOptions, type WebXRMotionControllerTeleportation } from "./features/WebXRControllerTeleportation";
import { type IWebXRDepthSensingOptions, type WebXRDepthSensing } from "./features/WebXRDepthSensing";
import { type IWebXRDomOverlayOptions, type WebXRDomOverlay } from "./features/WebXRDOMOverlay";
import { type WebXREyeTracking } from "./features/WebXREyeTracking";
import { type WebXRFeaturePointSystem } from "./features/WebXRFeaturePointSystem";
import { type IWebXRHandTrackingOptions, type WebXRHandTracking } from "./features/WebXRHandTracking";
import { type IWebXRHitTestOptions, type WebXRHitTest } from "./features/WebXRHitTest";
import { type IWebXRImageTrackingOptions, type WebXRImageTracking } from "./features/WebXRImageTracking";
import { type IWebXRLayersOptions, type WebXRLayers } from "./features/WebXRLayers";
import { type IWebXRLightEstimationOptions, type WebXRLightEstimation } from "./features/WebXRLightEstimation";
import { type IWebXRMeshDetectorOptions, type WebXRMeshDetector } from "./features/WebXRMeshDetector";
import { type IWebXRNearInteractionOptions, type WebXRNearInteraction } from "./features/WebXRNearInteraction";
import { type IWebXRPlaneDetectorOptions, type WebXRPlaneDetector } from "./features/WebXRPlaneDetector";
import { type IWebXRRawCameraAccessOptions, type WebXRRawCameraAccess } from "./features/WebXRRawCameraAccess";
import { type WebXRSpaceWarp } from "./features/WebXRSpaceWarp";
import { type IWebXRWalkingLocomotionOptions, type WebXRWalkingLocomotion } from "./features/WebXRWalkingLocomotion";
import { _ConsumeWebXRFeatureSpecificDisableWarning } from "./webXRFeatureWarningRegistry";
import { type WebXRSessionManager } from "./webXRSessionManager";

/**
 * Defining the interface required for a (webxr) feature
 */
export interface IWebXRFeature extends IDisposable {
    /**
     * Is this feature attached
     */
    attached: boolean;
    /**
     * Should auto-attach be disabled?
     */
    disableAutoAttach: boolean;
    /**
     * The auto-attach policy in effect before the features manager starts a manual attachment.
     * @internal
     */
    _autoAttachPolicyBeforeAttach?: boolean;

    /**
     * Attach the feature to the session
     * Will usually be called by the features manager
     *
     * @param force should attachment be forced (even when already attached)
     * @returns true if successful.
     */
    attach(force?: boolean): boolean;
    /**
     * Detach the feature from the session
     * Will usually be called by the features manager
     *
     * @returns true if successful.
     */
    detach(): boolean;

    /**
     * This function will be executed during before enabling the feature and can be used to not-allow enabling it.
     * Note that at this point the session has NOT started, so this is purely checking if the browser supports it
     *
     * @returns whether or not the feature is compatible in this environment
     */
    isCompatible(): boolean;

    /**
     * Was this feature disposed;
     */
    isDisposed: boolean;

    /**
     * The name of the native xr feature name, if applicable (like anchor, hit-test, or hand-tracking)
     */
    xrNativeFeatureName?: string;

    /**
     * A list of (Babylon WebXR) features this feature depends on
     */
    dependsOn?: string[];

    /**
     * If this feature requires to extend the XRSessionInit object, this function will return the partial XR session init object
     */
    getXRSessionInitExtension?: () => Promise<Partial<XRSessionInit>>;

    /**
     * Triggered when the feature is attached
     */
    onFeatureAttachObservable: Observable<IWebXRFeature>;
    /**
     * Triggered when the feature is detached
     */
    onFeatureDetachObservable: Observable<IWebXRFeature>;
}

/**
 * A list of the currently available features without referencing them
 */
export class WebXRFeatureName {
    /**
     * The name of the anchor system feature
     */
    public static readonly ANCHOR_SYSTEM = "xr-anchor-system" as const;
    /**
     * The name of the background remover feature
     */
    public static readonly BACKGROUND_REMOVER = "xr-background-remover" as const;
    /**
     * The name of the hit test feature
     */
    public static readonly HIT_TEST = "xr-hit-test" as const;
    /**
     * The name of the mesh detection feature
     */
    public static readonly MESH_DETECTION = "xr-mesh-detection" as const;
    /**
     * physics impostors for xr controllers feature
     */
    public static readonly PHYSICS_CONTROLLERS = "xr-physics-controller" as const;
    /**
     * The name of the plane detection feature
     */
    public static readonly PLANE_DETECTION = "xr-plane-detection" as const;
    /**
     * The name of the pointer selection feature
     */
    public static readonly POINTER_SELECTION = "xr-controller-pointer-selection" as const;
    /**
     * The name of the teleportation feature
     */
    public static readonly TELEPORTATION = "xr-controller-teleportation" as const;
    /**
     * The name of the feature points feature.
     */
    public static readonly FEATURE_POINTS = "xr-feature-points" as const;
    /**
     * The name of the hand tracking feature.
     */
    public static readonly HAND_TRACKING = "xr-hand-tracking" as const;
    /**
     * The name of the image tracking feature
     */
    public static readonly IMAGE_TRACKING = "xr-image-tracking" as const;
    /**
     * The name of the near interaction feature
     */
    public static readonly NEAR_INTERACTION = "xr-near-interaction" as const;
    /**
     * The name of the DOM overlay feature
     */
    public static readonly DOM_OVERLAY = "xr-dom-overlay" as const;
    /**
     * The name of the movement feature
     */
    public static readonly MOVEMENT = "xr-controller-movement" as const;
    /**
     * The name of the light estimation feature
     */
    public static readonly LIGHT_ESTIMATION = "xr-light-estimation" as const;
    /**
     * The name of the eye tracking feature
     */
    public static readonly EYE_TRACKING = "xr-eye-tracking" as const;
    /**
     * The name of the walking locomotion feature
     */
    public static readonly WALKING_LOCOMOTION = "xr-walking-locomotion" as const;
    /**
     * The name of the composition layers feature
     */
    public static readonly LAYERS = "xr-layers" as const;
    /**
     * The name of the depth sensing feature
     */
    public static readonly DEPTH_SENSING = "xr-depth-sensing" as const;
    /**
     * The name of the WebXR Space Warp feature
     */
    public static readonly SPACE_WARP = "xr-space-warp" as const;
    /**
     * The name of the WebXR Raw Camera Access feature
     */
    public static readonly RAW_CAMERA_ACCESS = "xr-raw-camera-access" as const;
    /**
     * The name of the body tracking feature
     */
    public static readonly BODY_TRACKING = "xr-body-tracking" as const;
}

export type WebXRFeatureNameType = (typeof WebXRFeatureName)[Exclude<keyof typeof WebXRFeatureName, "prototype">];

/**
 * Maps feature names to their corresponding feature implementation classes.
 */
export interface IWebXRFeatureNameTypeMap {
    /** Anchor system feature implementation. */
    [WebXRFeatureName.ANCHOR_SYSTEM]: WebXRAnchorSystem;
    /** Background remover feature implementation. */
    [WebXRFeatureName.BACKGROUND_REMOVER]: WebXRBackgroundRemover;
    /** Depth sensing feature implementation. */
    [WebXRFeatureName.DEPTH_SENSING]: WebXRDepthSensing;
    /** DOM overlay feature implementation. */
    [WebXRFeatureName.DOM_OVERLAY]: WebXRDomOverlay;
    /** Eye tracking feature implementation. */
    [WebXRFeatureName.EYE_TRACKING]: WebXREyeTracking;
    /** Feature points feature implementation. */
    [WebXRFeatureName.FEATURE_POINTS]: WebXRFeaturePointSystem;
    /** Hand tracking feature implementation. */
    [WebXRFeatureName.HAND_TRACKING]: WebXRHandTracking;
    /** Hit test feature implementation. */
    [WebXRFeatureName.HIT_TEST]: WebXRHitTest;
    /** Image tracking feature implementation. */
    [WebXRFeatureName.IMAGE_TRACKING]: WebXRImageTracking;
    /** Layers feature implementation. */
    [WebXRFeatureName.LAYERS]: WebXRLayers;
    /** Light estimation feature implementation. */
    [WebXRFeatureName.LIGHT_ESTIMATION]: WebXRLightEstimation;
    /** Mesh detection feature implementation. */
    [WebXRFeatureName.MESH_DETECTION]: WebXRMeshDetector;
    /** Controller movement feature implementation. */
    [WebXRFeatureName.MOVEMENT]: WebXRControllerMovement;
    /** Near interaction feature implementation. */
    [WebXRFeatureName.NEAR_INTERACTION]: WebXRNearInteraction;
    /** Physics controllers feature implementation. */
    [WebXRFeatureName.PHYSICS_CONTROLLERS]: WebXRControllerPhysics;
    /** Plane detection feature implementation. */
    [WebXRFeatureName.PLANE_DETECTION]: WebXRPlaneDetector;
    /** Controller pointer selection feature implementation. */
    [WebXRFeatureName.POINTER_SELECTION]: WebXRControllerPointerSelection;
    /** Raw camera access feature implementation. */
    [WebXRFeatureName.RAW_CAMERA_ACCESS]: WebXRRawCameraAccess;
    /** Space warp feature implementation. */
    [WebXRFeatureName.SPACE_WARP]: WebXRSpaceWarp;
    /** Teleportation feature implementation. */
    [WebXRFeatureName.TELEPORTATION]: WebXRMotionControllerTeleportation;
    /** Walking locomotion feature implementation. */
    [WebXRFeatureName.WALKING_LOCOMOTION]: WebXRWalkingLocomotion;
    /** Body tracking feature implementation. */
    [WebXRFeatureName.BODY_TRACKING]: WebXRBodyTracking;
}

/**
 * Maps feature names to their corresponding options interfaces.
 */
export interface IWebXRFeatureNameOptionsMap {
    /** Anchor system feature options. */
    [WebXRFeatureName.ANCHOR_SYSTEM]: IWebXRAnchorSystemOptions;
    /** Background remover feature options. */
    [WebXRFeatureName.BACKGROUND_REMOVER]: IWebXRBackgroundRemoverOptions;
    /** Depth sensing feature options. */
    [WebXRFeatureName.DEPTH_SENSING]: IWebXRDepthSensingOptions;
    /** DOM overlay feature options. */
    [WebXRFeatureName.DOM_OVERLAY]: IWebXRDomOverlayOptions;
    /** Eye tracking feature options. */
    [WebXRFeatureName.EYE_TRACKING]: undefined;
    /** Feature points feature options. */
    [WebXRFeatureName.FEATURE_POINTS]: undefined;
    /** Hand tracking feature options. */
    [WebXRFeatureName.HAND_TRACKING]: IWebXRHandTrackingOptions;
    /** Hit test feature options. */
    [WebXRFeatureName.HIT_TEST]: IWebXRHitTestOptions;
    /** Image tracking feature options. */
    [WebXRFeatureName.IMAGE_TRACKING]: IWebXRImageTrackingOptions;
    /** Layers feature options. */
    [WebXRFeatureName.LAYERS]: IWebXRLayersOptions;
    /** Light estimation feature options. */
    [WebXRFeatureName.LIGHT_ESTIMATION]: IWebXRLightEstimationOptions;
    /** Mesh detection feature options. */
    [WebXRFeatureName.MESH_DETECTION]: IWebXRMeshDetectorOptions;
    /** Controller movement feature options. */
    [WebXRFeatureName.MOVEMENT]: IWebXRControllerMovementOptions;
    /** Near interaction feature options. */
    [WebXRFeatureName.NEAR_INTERACTION]: IWebXRNearInteractionOptions;
    /** Physics controllers feature options. */
    [WebXRFeatureName.PHYSICS_CONTROLLERS]: IWebXRControllerPhysicsOptions;
    /** Plane detection feature options. */
    [WebXRFeatureName.PLANE_DETECTION]: IWebXRPlaneDetectorOptions;
    /** Controller pointer selection feature options. */
    [WebXRFeatureName.POINTER_SELECTION]: IWebXRControllerPointerSelectionOptions;
    /** Raw camera access feature options. */
    [WebXRFeatureName.RAW_CAMERA_ACCESS]: IWebXRRawCameraAccessOptions;
    /** Space warp feature options. */
    [WebXRFeatureName.SPACE_WARP]: undefined;
    /** Teleportation feature options. */
    [WebXRFeatureName.TELEPORTATION]: IWebXRTeleportationOptions;
    /** Walking locomotion feature options. */
    [WebXRFeatureName.WALKING_LOCOMOTION]: IWebXRWalkingLocomotionOptions;
    /** Body tracking feature options. */
    [WebXRFeatureName.BODY_TRACKING]: IWebXRBodyTrackingOptions;
}

/**
 * Helper type that expands/flattens a type to show its properties inline in IntelliSense
 */
type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

/**
 * Helper type to resolve the specific feature type based on the feature name,
 * or fallback to IWebXRFeature if the feature name is not in the type map.
 */
export type ResolveWebXRFeature<T extends WebXRFeatureNameType> = T extends keyof IWebXRFeatureNameTypeMap ? IWebXRFeatureNameTypeMap[T] : IWebXRFeature;

/**
 * Helper type to resolve the options type for a specific feature based on the feature name,
 * or fallback to any if the feature name is not in the type map.
 * The Expand utility type flattens the interface to show properties inline in IntelliSense.
 */
export type ResolveWebXRFeatureOptions<T extends WebXRFeatureNameType> = T extends keyof IWebXRFeatureNameOptionsMap
    ? IWebXRFeatureNameOptionsMap[T] extends undefined
        ? undefined
        : Expand<IWebXRFeatureNameOptionsMap[T]>
    : any;

/**
 * Defining the constructor of a feature. Used to register the modules.
 */
export type WebXRFeatureConstructor = (xrSessionManager: WebXRSessionManager, options?: any) => () => IWebXRFeature;

/**
 * The WebXR features manager is responsible of enabling or disabling features required for the current XR session.
 * It is mainly used in AR sessions.
 *
 * A feature can have a version that is defined by Babylon (and does not correspond with the webxr version).
 */
export class WebXRFeaturesManager implements IDisposable {
    private static readonly _AvailableFeatures: {
        [name: string]: {
            stable: number;
            latest: number;
            [version: number]: WebXRFeatureConstructor;
        };
    } = {};

    private _features: {
        [name: string]: {
            featureImplementation: IWebXRFeature;
            version: number;
            enabled: boolean;
            required: boolean;
        };
    } = {};

    /**
     * The key is the feature to check and the value is the feature that conflicts.
     */
    private static readonly _ConflictingFeatures: { [key: string]: string } = {
        [WebXRFeatureName.TELEPORTATION]: WebXRFeatureName.MOVEMENT,
        [WebXRFeatureName.MOVEMENT]: WebXRFeatureName.TELEPORTATION,
    };

    /**
     * constructs a new features manages.
     *
     * @param _xrSessionManager an instance of WebXRSessionManager
     */
    constructor(private _xrSessionManager: WebXRSessionManager) {
        // when session starts / initialized - attach
        this._xrSessionManager.onXRSessionInit.add(() => {
            const features = this.getEnabledFeatures();
            for (const featureName of features) {
                const feature = this._features[featureName];
                if (feature.enabled && !feature.featureImplementation.attached && !feature.featureImplementation.disableAutoAttach) {
                    this.attachFeature(featureName);
                }
            }
        });

        // when session ends - detach
        this._xrSessionManager.onXRSessionEnded.add(() => {
            const features = this.getEnabledFeatures();
            for (const featureName of features) {
                const feature = this._features[featureName];
                if (feature.enabled && feature.featureImplementation.attached) {
                    // detach, but don't disable!
                    this.detachFeature(featureName);
                }
            }
        });
    }

    /**
     * Used to register a module. After calling this function a developer can use this feature in the scene.
     * Mainly used internally.
     *
     * @param featureName the name of the feature to register
     * @param constructorFunction the function used to construct the module
     * @param version the (babylon) version of the module
     * @param stable is that a stable version of this module
     */
    public static AddWebXRFeature(featureName: string, constructorFunction: WebXRFeatureConstructor, version: number = 1, stable: boolean = false) {
        this._AvailableFeatures[featureName] = this._AvailableFeatures[featureName] || { latest: version };
        if (version > this._AvailableFeatures[featureName].latest) {
            this._AvailableFeatures[featureName].latest = version;
        }
        if (stable) {
            this._AvailableFeatures[featureName].stable = version;
        }
        this._AvailableFeatures[featureName][version] = constructorFunction;
    }

    /**
     * Returns a constructor of a specific feature.
     *
     * @param featureName the name of the feature to construct
     * @param version the version of the feature to load
     * @param xrSessionManager the xrSessionManager. Used to construct the module
     * @param options optional options provided to the module.
     * @returns a function that, when called, will return a new instance of this feature
     */
    public static ConstructFeature(featureName: string, version: number = 1, xrSessionManager: WebXRSessionManager, options?: any): () => IWebXRFeature {
        const constructorFunction = this._AvailableFeatures[featureName][version];
        if (!constructorFunction) {
            // throw an error? return nothing?
            throw new Error("feature not found");
        }

        return constructorFunction(xrSessionManager, options);
    }

    /**
     * Can be used to return the list of features currently registered
     *
     * @returns an Array of available features
     */
    public static GetAvailableFeatures() {
        return Object.keys(this._AvailableFeatures);
    }

    /**
     * Gets the versions available for a specific feature
     * @param featureName the name of the feature
     * @returns an array with the available versions
     */
    public static GetAvailableVersions(featureName: string) {
        return Object.keys(this._AvailableFeatures[featureName]);
    }

    /**
     * Return the latest unstable version of this feature
     * @param featureName the name of the feature to search
     * @returns the version number. if not found will return -1
     */
    public static GetLatestVersionOfFeature(featureName: string): number {
        return (this._AvailableFeatures[featureName] && this._AvailableFeatures[featureName].latest) || -1;
    }

    /**
     * Return the latest stable version of this feature
     * @param featureName the name of the feature to search
     * @returns the version number. if not found will return -1
     */
    public static GetStableVersionOfFeature(featureName: string): number {
        return (this._AvailableFeatures[featureName] && this._AvailableFeatures[featureName].stable) || -1;
    }

    /**
     * Attach a feature to the current session. Mainly used when session started to start the feature effect.
     * Can be used during a session to start a feature
     * @param featureName the name of feature to attach
     */
    public attachFeature(featureName: string) {
        const feature = this._features[featureName];
        if (feature && feature.enabled && !feature.featureImplementation.attached) {
            const disableAutoAttachBeforeAttach = feature.featureImplementation.disableAutoAttach;
            feature.featureImplementation._autoAttachPolicyBeforeAttach = disableAutoAttachBeforeAttach;
            feature.featureImplementation.disableAutoAttach = false;
            let attached: boolean;
            try {
                attached = feature.featureImplementation.attach();
            } finally {
                delete feature.featureImplementation._autoAttachPolicyBeforeAttach;
            }
            const intentionallyDisabledDuringAttach = feature.featureImplementation.disableAutoAttach;
            if (!intentionallyDisabledDuringAttach) {
                feature.featureImplementation.disableAutoAttach = disableAutoAttachBeforeAttach;
            }
            if (!attached && !intentionallyDisabledDuringAttach) {
                Tools.Warn(`Feature ${featureName} failed to attach`);
            }
        }
    }

    /**
     * Can be used inside a session or when the session ends to detach a specific feature
     * @param featureName the name of the feature to detach
     */
    public detachFeature(featureName: string) {
        const feature = this._features[featureName];
        if (feature && feature.featureImplementation.attached) {
            const detached = feature.featureImplementation.detach();
            if (!detached) {
                Tools.Warn(`Feature ${featureName} failed to detach`);
            }
        }
    }

    /**
     * Used to disable an already-enabled feature
     * The feature will be disposed and will be recreated once enabled.
     * @param featureName the feature to disable
     * @returns true if disable was successful
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public disableFeature(featureName: string | { Name: string }): boolean {
        const name = typeof featureName === "string" ? featureName : featureName.Name;
        const feature = this._features[name];
        if (feature && feature.enabled) {
            feature.enabled = false;
            this.detachFeature(name);
            feature.featureImplementation.dispose();
            delete this._features[name];
            return true;
        }
        return false;
    }

    /**
     * dispose this features manager
     */
    public dispose(): void {
        const features = this.getEnabledFeatures();
        for (const featureName of features) {
            this.disableFeature(featureName);
        }
    }

    /**
     * Enable a feature using its name and a version. This will enable it in the scene, and will be responsible to attach it when the session starts.
     * If used twice, the old version will be disposed and a new one will be constructed. This way you can re-enable with different configuration.
     *
     * @param featureName the name of the feature to load or the class of the feature
     * @param version optional version to load. if not provided the latest version will be enabled
     * @param moduleOptions options provided to the module. Ses the module documentation / constructor
     * @param attachIfPossible if set to true (default) the feature will be automatically attached, if it is currently possible
     * @param required is this feature required to the app. If set to true the session init will fail if the feature is not available.
     * @returns a new constructed feature or throws an error if feature not found or conflicts with another enabled feature.
     */
    public enableFeature<T extends WebXRFeatureNameType>(
        // eslint-disable-next-line @typescript-eslint/naming-convention
        featureName: T | { Name: T },
        version: number | string = "latest",
        moduleOptions: ResolveWebXRFeatureOptions<T> = {} as ResolveWebXRFeatureOptions<T>,
        attachIfPossible: boolean = true,
        required: boolean = true
    ): ResolveWebXRFeature<T> {
        const name = typeof featureName === "string" ? featureName : featureName.Name;
        let versionToLoad: number;
        if (typeof version === "string") {
            if (!version) {
                throw new Error(`Error in provided version - ${name} (${version})`);
            }
            if (version === "stable") {
                versionToLoad = WebXRFeaturesManager.GetStableVersionOfFeature(name);
            } else if (version === "latest") {
                versionToLoad = WebXRFeaturesManager.GetLatestVersionOfFeature(name);
            } else {
                // try loading the number the string represents
                versionToLoad = +version;
            }
            if (versionToLoad === -1 || isNaN(versionToLoad)) {
                throw new Error(`feature not found - ${name} (${version})`);
            }
        } else {
            versionToLoad = version;
        }

        // check if there is a feature conflict
        const conflictingFeature = WebXRFeaturesManager._ConflictingFeatures[name];
        if (conflictingFeature !== undefined && this.getEnabledFeatures().indexOf(conflictingFeature) !== -1) {
            throw new Error(`Feature ${name} cannot be enabled while ${conflictingFeature} is enabled.`);
        }

        // check if already initialized
        const feature = this._features[name];
        const constructFunction = WebXRFeaturesManager.ConstructFeature(name, versionToLoad, this._xrSessionManager, moduleOptions);
        if (!constructFunction) {
            // report error?
            throw new Error(`feature not found - ${name}`);
        }

        /* If the feature is already enabled, detach and dispose it, and create a new one */
        if (feature) {
            this.disableFeature(name);
        }

        const constructed = constructFunction();
        if (constructed.dependsOn) {
            const dependentsFound = constructed.dependsOn.every((featureName) => !!this._features[featureName]);
            if (!dependentsFound) {
                throw new Error(`Dependant features missing. Make sure the following features are enabled - ${constructed.dependsOn.join(", ")}`);
            }
        }
        _ConsumeWebXRFeatureSpecificDisableWarning(constructed);
        const isCompatible = constructed.isCompatible();
        const emittedSpecificDisableWarning = _ConsumeWebXRFeatureSpecificDisableWarning(constructed);
        if (isCompatible) {
            this._features[name] = {
                featureImplementation: constructed,
                enabled: true,
                version: versionToLoad,
                required,
            };

            if (attachIfPossible) {
                // if session started already, request and enable
                if (this._xrSessionManager.session && !this._features[name].featureImplementation.attached) {
                    // enable feature
                    this.attachFeature(name);
                }
            } else {
                // disable auto-attach when session starts
                this._features[name].featureImplementation.disableAutoAttach = true;
            }

            return this._features[name].featureImplementation as ResolveWebXRFeature<T>;
        } else {
            if (required) {
                throw new Error("required feature not compatible");
            } else {
                if (!emittedSpecificDisableWarning) {
                    Tools.Warn(`Feature ${name} not compatible with the current environment/browser and was not enabled.`);
                }
                return constructed as ResolveWebXRFeature<T>;
            }
        }
    }

    /**
     * get the implementation of an enabled feature.
     * @param featureName the name of the feature to load
     * @returns the feature class, if found
     */
    public getEnabledFeature<T extends WebXRFeatureNameType>(featureName: T): ResolveWebXRFeature<T> {
        return this._features[featureName] && (this._features[featureName].featureImplementation as ResolveWebXRFeature<T>);
    }

    /**
     * Get the list of enabled features
     * @returns an array of enabled features
     */
    public getEnabledFeatures() {
        return Object.keys(this._features);
    }

    /**
     * This function will extend the session creation configuration object with enabled features.
     * If, for example, the anchors feature is enabled, it will be automatically added to the optional or required features list,
     * according to the defined "required" variable, provided during enableFeature call
     * @param xrSessionInit the xr Session init object to extend
     *
     * @returns an extended XRSessionInit object
     */
    public async _extendXRSessionInitObject(xrSessionInit: XRSessionInit): Promise<XRSessionInit> {
        const enabledFeatures = this.getEnabledFeatures();
        for (const featureName of enabledFeatures) {
            const feature = this._features[featureName];
            const nativeName = feature.featureImplementation.xrNativeFeatureName;
            if (nativeName) {
                if (feature.required) {
                    xrSessionInit.requiredFeatures = xrSessionInit.requiredFeatures || [];
                    if (xrSessionInit.requiredFeatures.indexOf(nativeName) === -1) {
                        xrSessionInit.requiredFeatures.push(nativeName);
                    }
                } else {
                    xrSessionInit.optionalFeatures = xrSessionInit.optionalFeatures || [];
                    if (xrSessionInit.optionalFeatures.indexOf(nativeName) === -1) {
                        xrSessionInit.optionalFeatures.push(nativeName);
                    }
                }
            }
            if (feature.featureImplementation.getXRSessionInitExtension) {
                // eslint-disable-next-line no-await-in-loop
                const extended = await feature.featureImplementation.getXRSessionInitExtension();
                xrSessionInit = {
                    ...xrSessionInit,
                    ...extended,
                };
            }
        }
        return xrSessionInit;
    }
}
